import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { nodeTabClass } from '../shared/railClasses';
import SPAPill from '../shared/SPAPill';

export type Section = 'students' | 'classes' | 'copies';

export interface SectionTab {
  key: Section;
  label: string;
  /** Something in the tab needs attention, said on the tab so it is not missed while hidden. */
  alert?: string | null;
}

/** `?tab=…` for the open section, for dialogs opened from it to carry there and back. */
export const useSectionSearch = () => {
  const [params] = useSearchParams();
  const tab = params.get('tab');

  return tab == null ? '' : `?tab=${tab}`;
};

// Links rather than state, so a pasted link opens the same section (the page keeps it in ?tab=).
// Switching drops ?verify=1, which only focuses the check once after a repair.
const SectionTabs = ({ active, tabs }: { active: Section; tabs: SectionTab[] }) => {
  const [params] = useSearchParams();

  const searchFor = (key: Section) => {
    const next = new URLSearchParams(params);
    next.delete('verify');
    if (key === 'students') next.delete('tab');
    else next.set('tab', key);
    const query = next.toString();

    return query === '' ? '' : `?${query}`;
  };

  return (
    <div className="tw-mb-4 tw-border-0 tw-border-b tw-border-solid tw-border-spa-border">
      <nav aria-label="Assignment sections" className="tw-flex tw-flex-wrap tw-gap-0.5">
        {tabs.map(tab => (
          <Link
            key={tab.key}
            replace
            aria-current={tab.key === active ? 'page' : undefined}
            className={nodeTabClass(tab.key === active)}
            to={{ search: searchFor(tab.key) }}
          >
            {tab.label}
            {tab.alert != null && <SPAPill tone="warning">{tab.alert}</SPAPill>}
          </Link>
        ))}
      </nav>
    </div>
  );
};

export default SectionTabs;
